import React, { useState, useRef } from "react";
import { Upload, FileText, ArrowRight, CheckCircle2, Lock, LogOut, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/config";
import { useAuth, getAuthToken } from "../../lib/auth";

function LoginGate() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password are required.");
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in. Welcome back.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sign-in failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="border border-slate-200 p-6 lg:p-8 bg-white max-w-md"
      data-testid="tech-login-form"
    >
      <div className="flex items-center gap-2 text-slate-900">
        <Lock className="w-4 h-4" />
        <span className="overline">Tech Portal · Sign in required</span>
      </div>
      <h3 className="font-display text-2xl font-bold tracking-tight mt-1">Sign In</h3>
      <p className="text-sm text-slate-600 mt-1">
        Only authorized backflow technicians can submit test reports.
      </p>

      <div className="grid gap-4 mt-5">
        <div>
          <label className="field-label">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="lb-input"
            autoComplete="username"
            required
            data-testid="tech-login-email"
          />
        </div>
        <div>
          <label className="field-label">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="lb-input"
            autoComplete="current-password"
            required
            data-testid="tech-login-password"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="btn-secondary w-full mt-5 justify-center disabled:opacity-60"
        data-testid="tech-login-submit"
      >
        {loading ? "Signing in…" : "Sign In"} <ArrowRight className="w-4 h-4" />
      </button>

      <p className="text-xs text-slate-500 mt-3 flex items-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5" /> 8-hour session · encrypted token
      </p>
    </form>
  );
}

function TestReportForm() {
  const { user, logout } = useAuth();
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [form, setForm] = useState({
    tech_name: user?.name || "",
    tech_cert_number: "",
    tech_email: user?.email || "",
    customer_name: "",
    property_address: "",
    device_type: "RPZ",
    device_serial: "",
    test_date: "",
    test_result: "Pass",
    notes: "",
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setFileName("");
    if (f.size > 15 * 1024 * 1024) {
      toast.error("File too large. Max 15MB.");
      e.target.value = "";
      return setFileName("");
    }
    setFileName(f.name);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.tech_name || !form.customer_name || !form.property_address || !form.test_date) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      const file = fileRef.current?.files?.[0];
      if (file) fd.append("file", file);

      const token = getAuthToken();
      const res = await fetch(`${API}/test-reports`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
        return;
      }
      if (!res.ok) throw new Error("Upload failed");
      toast.success("Test report submitted and emailed.");
      setForm({
        tech_name: user?.name || "",
        tech_cert_number: "",
        tech_email: user?.email || "",
        customer_name: "",
        property_address: "",
        device_type: "RPZ",
        device_serial: "",
        test_date: "",
        test_result: "Pass",
        notes: "",
      });
      setFileName("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="border border-slate-200 p-6 lg:p-8 bg-white"
      data-testid="test-report-form"
      encType="multipart/form-data"
    >
      <div className="flex items-center justify-between gap-2 mb-4 pb-4 border-b border-slate-200">
        <div className="flex items-center gap-2 text-sm">
          <ShieldCheck className="w-4 h-4 text-green-600" />
          <span className="font-display font-semibold">Signed in as {user?.name || user?.email}</span>
        </div>
        <button
          type="button"
          onClick={logout}
          className="text-xs font-display font-semibold text-slate-600 hover:text-red-600 inline-flex items-center gap-1"
          data-testid="tech-logout-btn"
        >
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="field-label">Technician Name *</label>
          <input name="tech_name" value={form.tech_name} onChange={onChange} className="lb-input" required data-testid="tr-tech-name" />
        </div>
        <div>
          <label className="field-label">BPAT Cert #</label>
          <input name="tech_cert_number" value={form.tech_cert_number} onChange={onChange} className="lb-input" data-testid="tr-cert" />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Tech Email</label>
          <input name="tech_email" type="email" value={form.tech_email} onChange={onChange} className="lb-input" data-testid="tr-tech-email" />
        </div>
        <div>
          <label className="field-label">Customer Name *</label>
          <input name="customer_name" value={form.customer_name} onChange={onChange} className="lb-input" required data-testid="tr-customer-name" />
        </div>
        <div>
          <label className="field-label">Test Date *</label>
          <input name="test_date" type="date" value={form.test_date} onChange={onChange} className="lb-input" required data-testid="tr-test-date" />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Property Address *</label>
          <input name="property_address" value={form.property_address} onChange={onChange} className="lb-input" required data-testid="tr-property-address" />
        </div>
        <div>
          <label className="field-label">Device Type *</label>
          <select name="device_type" value={form.device_type} onChange={onChange} className="lb-select" data-testid="tr-device-type">
            <option>RPZ</option>
            <option>DC</option>
            <option>PVB</option>
            <option>DCDA</option>
            <option>SVB</option>
          </select>
        </div>
        <div>
          <label className="field-label">Device Serial #</label>
          <input name="device_serial" value={form.device_serial} onChange={onChange} className="lb-input" data-testid="tr-serial" />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Test Result *</label>
          <select name="test_result" value={form.test_result} onChange={onChange} className="lb-select" data-testid="tr-result">
            <option>Pass</option>
            <option>Fail</option>
            <option>Repaired &amp; Retested — Pass</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Notes</label>
          <textarea name="notes" value={form.notes} onChange={onChange} rows={3} className="lb-textarea" data-testid="tr-notes" />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Attach Report (PDF)</label>
          <div className="border border-dashed border-slate-300 p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="w-5 h-5 text-blue-600 shrink-0" />
              <span className="text-sm text-slate-600 truncate">
                {fileName || "No file selected. PDF up to 15MB."}
              </span>
            </div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="btn-ghost !py-2 !px-4 text-sm"
              data-testid="tr-file-pick"
            >
              <Upload className="w-4 h-4" /> Choose file
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              onChange={handleFile}
              data-testid="tr-file-input"
            />
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="btn-secondary w-full mt-5 justify-center disabled:opacity-60"
        data-testid="tr-submit-btn"
      >
        {loading ? "Submitting..." : "Submit Test Report"} <ArrowRight className="w-4 h-4" />
      </button>
    </form>
  );
}

export default function TestReportSection() {
  const { user, loading } = useAuth();

  return (
    <section id="test-report" className="py-20 lg:py-28" data-testid="test-report-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[1fr_1.1fr] gap-12">
        <div>
          <div className="step-num">05 — TECH PORTAL</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold mt-3 tracking-tight">
            Submit a completed test report.
          </h2>
          <p className="text-slate-600 mt-4 max-w-md">
            Restricted area — only authorized backflow technicians. Sign in, complete the form, attach
            the signed PDF, and we'll file it with the City of Laredo and email a copy to the office.
          </p>

          <ul className="mt-8 space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5" /> Login-protected · session expires in 8 hours
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5" /> PDF accepted up to 15MB
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5" /> Emailed immediately to the office
            </li>
          </ul>
        </div>

        <div>
          {loading ? (
            <div
              className="border border-slate-200 p-8 bg-white text-slate-500 text-sm"
              data-testid="tech-portal-loading"
            >
              Checking session…
            </div>
          ) : user ? (
            <TestReportForm />
          ) : (
            <LoginGate />
          )}
        </div>
      </div>
    </section>
  );
}
